import { RangeApi } from 'platejs';
import type { PlateEditor } from 'platejs/react';

/**
 * True when text is effectively empty for formatting-affordance purposes: '',
 * whitespace-only, or Plate's empty-paragraph marker (`\u200B`).
 * JS `.trim()` does not strip `\u200B`, so empty editors that normalize to that
 * marker would otherwise look "non-empty".
 */
export function isBlankText(text: string): boolean {
  return text.replaceAll('\u200B', '').trim() === '';
}

/**
 * Serialized markdown with an all-blank document collapsed to ''. Callers that
 * gate UI or requests on "has the user written anything" must use this: Plate
 * serializes blank paragraphs as `\u200B`, which survives `.trim()`, so a
 * visually empty editor otherwise reads as having content.
 */
export function normalizeBlankMarkdown(markdown: string): string {
  return isBlankText(markdown) ? '' : markdown;
}

/**
 * The block the empty-paragraph Aa toggle anchors to, or undefined when the
 * toggle shouldn't render:
 * - Caret collapsed in a blank block → that block
 * - No selection yet, but the whole document is blank → the first block
 * - Expanded selection → nothing (the selection toolbar owns that state)
 */
export function getEmptyParagraphAaBlock(editor: PlateEditor) {
  const selection = editor.selection;
  if (selection && RangeApi.isExpanded(selection)) return undefined;
  if (!selection) {
    if (!isBlankText(editor.api.string([]))) return undefined;
    return editor.api.block({ at: [0] });
  }

  const entry = editor.api.block({ at: selection });
  if (!entry) return undefined;

  return isBlankText(editor.api.string(entry[1])) ? entry : undefined;
}

/** Whether the empty-paragraph Aa toggle should render. */
export function getShouldShowEmptyParagraphAa(editor: PlateEditor): boolean {
  return Boolean(getEmptyParagraphAaBlock(editor));
}
